import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Search, Copy, Download, FileCode2, ShieldCheck } from "lucide-react";
import { CHAT_PROMPTS } from "../data/generated/chat-prompts.generated";
import {
  PROMPT_CATALOG_YAML,
  PROMPT_CATALOG_SOURCE_PATH,
  PROMPT_CATALOG_LINE_COUNT,
  PROMPT_CATALOG_BYTE_SIZE,
} from "../data/generated/prompt-catalog.generated";
import { tokenizeYamlLine, TOKEN_CLASS } from "../lib/yamlHighlight";
import { OptimizerPanel } from "../components/admin/OptimizerPanel";
import { cls } from "../lib/cls";

export default function AdminPrompts(): JSX.Element {
  const [query, setQuery] = useState("");
  const [copied, setCopied] = useState(false);

  const lines = useMemo(() => PROMPT_CATALOG_YAML.split(/\r?\n/), []);
  const filteredLineSet = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    const s = new Set<number>();
    lines.forEach((l: string, i: number) => { if (l.toLowerCase().includes(q)) s.add(i); });
    return s;
  }, [query, lines]);

  function downloadYaml(): void {
    const blob = new Blob([PROMPT_CATALOG_YAML], { type: "text/yaml" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "chat-prompts.yaml";
    a.click();
    URL.revokeObjectURL(a.href);
  }
  function copyYaml(): void {
    navigator.clipboard.writeText(PROMPT_CATALOG_YAML).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <div className="max-w-[1280px] mx-auto px-4 sm:px-8 py-10">
      <header className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="eyebrow inline-flex items-center gap-1.5">
            <ShieldCheck className="w-3 h-3" /> Admin · prompt catalog
          </div>
          <h1 className="display-2 mt-1 text-ink-950">Prompts. Single source. YAML.</h1>
          <p className="text-[13px] text-ink-500 mt-1 max-w-2xl">
            What the cockpit reads is what the agent reads. This page renders the same
            <code className="font-mono text-ink-950 mx-1">{PROMPT_CATALOG_SOURCE_PATH}</code>
            file that <code className="font-mono text-ink-950">npm run gen:prompts</code> codegen's
            into TS — no duplication, no drift.
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="pill bg-cream-dark border border-border text-ink-700 tabular text-[10px]">
            {CHAT_PROMPTS.length} prompts
          </span>
          <span className="pill bg-cream-dark border border-border text-ink-700 tabular text-[10px]">
            {PROMPT_CATALOG_LINE_COUNT} lines
          </span>
          <span className="pill bg-cream-dark border border-border text-ink-700 tabular text-[10px]">
            {(PROMPT_CATALOG_BYTE_SIZE / 1024).toFixed(1)} KB
          </span>
        </div>
      </header>

      <PromptSummary />

      <OptimizerPanel />

      <div className="mt-6 panel p-3">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-500" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter lines (case-insensitive)…"
              className="w-full bg-cream border border-border rounded-lg pl-8 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foyr/30"
            />
          </div>
          <button onClick={copyYaml} className="btn btn-outline !text-xs !py-1.5">
            <Copy className="w-3 h-3" /> {copied ? "Copied" : "Copy YAML"}
          </button>
          <button onClick={downloadYaml} className="btn btn-outline !text-xs !py-1.5">
            <Download className="w-3 h-3" /> Download
          </button>
          <a
            href="https://github.com/cloudflare/workers-sdk"
            target="_blank"
            rel="noreferrer"
            className="pill bg-cream-dark border border-border text-ink-500 hover:border-foyr/40 hover:text-ink-950"
            title="The single source of truth for both the agent and the cockpit"
          >
            <FileCode2 className="w-3 h-3" /> source-of-truth
          </a>
        </div>
        <YamlView lines={lines} filtered={filteredLineSet} />
      </div>
    </div>
  );
}

function PromptSummary(): JSX.Element {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
      {CHAT_PROMPTS.map((p, i) => (
        <motion.div
          key={p.id}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.04 }}
          className="panel p-3 space-y-1.5"
        >
          <div className="flex items-center gap-1.5">
            <span className="pill bg-ink-950 text-cream text-[10px] font-mono">{p.id}</span>
            <span className="pill bg-cream-dark border border-border text-ink-700 text-[10px] font-mono">
              {p.icon}
            </span>
          </div>
          <div className="text-[13px] font-semibold text-ink-950 leading-tight">{p.label}</div>
          <div className="text-[11px] text-ink-500 leading-snug">"{p.q}"</div>
          <div className="text-[10px] text-ink-500">
            slots: {p.slots.length} · tools: {p.tools_allowed.length} · match: {p.match.length}
          </div>
        </motion.div>
      ))}
    </div>
  );
}

function YamlView({ lines, filtered }: { lines: string[]; filtered: Set<number> | null }): JSX.Element {
  return (
    <div className="rounded-lg border border-border overflow-hidden bg-cream font-mono text-[12.5px] leading-[1.55]">
      <div className="max-h-[640px] overflow-auto">
        <table className="w-full">
          <tbody>
            {lines.map((line, i) => {
              const dim = filtered !== null && !filtered.has(i);
              const tokens = tokenizeYamlLine(line);
              return (
                <tr key={i} className={cls(dim ? "opacity-30" : "", i % 2 ? "bg-cream-dark/30" : "")}>
                  <td className="px-3 py-0 text-right text-ink-300 tabular select-none border-r border-border w-[3.5rem]">
                    {i + 1}
                  </td>
                  <td className="px-3 py-0 whitespace-pre">
                    {tokens.map((t, j) => (
                      <span key={j} className={TOKEN_CLASS[t.kind]}>{t.text}</span>
                    ))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
